import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, FileText, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('failed');
        setError('No payment session found');
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        
        if (res.ok) {
          const data = await res.json();
          setPaymentInfo(data);
          
          if (data.payment_status === 'paid') {
            setStatus('success');
          } else if (data.payment_status === 'unpaid') {
            // Payment might still be processing, retry
            setTimeout(checkPaymentStatus, 2000);
          } else {
            setStatus('failed');
            setError('Payment was not completed');
          }
        } else {
          setStatus('failed');
          setError('Could not verify payment status');
        }
      } catch (err) {
        console.error('Payment status check failed:', err);
        setStatus('failed');
        setError('Error checking payment status');
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center p-6">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Verifying Payment...</h1>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center p-6">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Issue</h1>
            <p className="text-muted-foreground mb-6">
              {error || 'There was a problem with your payment'}
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/request-service')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Try Again
              </Button>
              <p className="text-sm text-muted-foreground">
                Need help? Contact us at info@bylyngo.com
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center p-6">
      <Card className="w-full max-w-lg" data-testid="payment-success-card">
        <CardContent className="pt-8 pb-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground">
              Thank you for your payment
            </p>
          </div>

          {paymentInfo && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
              {paymentInfo.work_order_id && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Order Number</span>
                  <span className="font-semibold text-blue-600" data-testid="order-number">
                    {paymentInfo.work_order_id}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Amount Paid</span>
                <span className="font-semibold text-green-600" data-testid="amount-paid">
                  ${paymentInfo.amount_total?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Confirmed
                </span>
              </div>
            </div>
          )}

          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              What's Next?
            </h3>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>A confirmation email has been sent to you</li>
              <li>Our team will review your request</li>
              <li>We'll contact you within 24 hours</li>
            </ul>
          </div>

          <div className="space-y-3 text-center">
            <Button 
              onClick={() => navigate('/request-service')}
              variant="outline"
              className="w-full"
            >
              Submit Another Request
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="text-xs text-muted-foreground">
              Questions? Contact us at info@bylyngo.com
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-950 text-white py-4">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-blue-200 text-sm">
            © {new Date().getFullYear()} Bylyngo Interpreting & Translation
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
